import React, { useEffect, createContext, useContext, useReducer } from 'react';
import { getMe } from '../api/auth';
interface User {
  id: string;
  email: string;
  fullName?: string;
  [key: string]: any;
}
interface AuthState {
  user: User | null;
  token: string | null;
  isAuthenticated: boolean;
  isLoading: boolean;
}
type AuthAction = {
  type: 'RESTORE_SESSION';
  payload: {
    user: User;
    token: string;
  };
} | {
  type: 'LOGIN';
  payload: {
    user: User;
    token: string;
  };
} | {
  type: 'LOGOUT';
} | {
  type: 'SET_LOADING';
  payload: boolean;
};
const initialState: AuthState = {
  user: null,
  token: null,
  isAuthenticated: false,
  isLoading: true
};
function authReducer(state: AuthState, action: AuthAction): AuthState {
  switch (action.type) {
    case 'RESTORE_SESSION':
    case 'LOGIN':
      return {
        ...state,
        user: action.payload.user,
        token: action.payload.token,
        isAuthenticated: true,
        isLoading: false
      };
    case 'LOGOUT':
      return {
        ...state,
        user: null,
        token: null,
        isAuthenticated: false,
        isLoading: false
      };
    case 'SET_LOADING':
      return {
        ...state,
        isLoading: action.payload
      };
    default:
      return state;
  }
}
interface AuthContextType extends AuthState {
  login: (token: string, user: User) => void;
  logout: () => void;
}
const AuthContext = createContext<AuthContextType | undefined>(undefined);
export function AuthProvider({
  children
}: {
  children: ReactNode;
}) {
  const [state, dispatch] = useReducer(authReducer, initialState);
  useEffect(() => {
    async function initAuth() {
      const token = localStorage.getItem('bella_admin_token');
      if (token) {
        try {
          const user = await getMe(token);
          dispatch({
            type: 'RESTORE_SESSION',
            payload: {
              user,
              token
            }
          });
        } catch (error) {
          console.error('Failed to restore session:', error);
          localStorage.removeItem('bella_admin_token');
          dispatch({
            type: 'LOGOUT'
          });
        }
      } else {
        dispatch({
          type: 'SET_LOADING',
          payload: false
        });
      }
    }
    initAuth();
  }, []);
  const login = (token: string, user: User) => {
    localStorage.setItem('bella_admin_token', token);
    dispatch({
      type: 'LOGIN',
      payload: {
        user,
        token
      }
    });
  };
  const logout = () => {
    localStorage.removeItem('bella_admin_token');
    dispatch({
      type: 'LOGOUT'
    });
  };
  return <AuthContext.Provider value={{
    ...state,
    login,
    logout
  }} data-id="element-1279">
      {children}
    </AuthContext.Provider>;
}
export function useAuth() {
  const context = useContext(AuthContext);
  if (context === undefined) {
    throw new Error('useAuth must be used within an AuthProvider');
  }
  return context;
}